// client/src/settings/CacheSettings.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { 
    useNotify, 
    Title 
} from 'react-admin';
import {
    Box,
    Typography,
    FormGroup,
    FormControlLabel,
    Switch,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Chip,
    Stack,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';

interface CacheStatus {
    enabled: boolean;
    ready: boolean;
    connected: boolean;
    totalKeys: number;
    keysByType?: {
        [key: string]: number;
    };
}

const CacheSettings: React.FC = () => {
    const notify = useNotify();
    const [loading, setLoading] = useState(true);
    const [clearing, setClearing] = useState(false);
    const [cacheStatus, setCacheStatus] = useState<CacheStatus | null>(null);
    const [enabled, setEnabled] = useState(false);

    const apiUrl = process.env.REACT_APP_API_URL || '';

    const fetchCacheStatus = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${apiUrl}/cache/status`);
            if (response.data.success && response.data.status) {
                const status = response.data.status;
                setCacheStatus(status);
                setEnabled(status.enabled || false);
            }
        } catch (error: any) {
            console.error('Error fetching cache status:', error);
            notify('Error loading cache status', { type: 'warning' });
        } finally {
            setLoading(false);
        }
    }, [apiUrl, notify]);

    // Fetch cache status on mount
    useEffect(() => {
        fetchCacheStatus();
    }, [fetchCacheStatus]);


    const handleClearCache = async (type: 'products' | 'masters' | 'all') => {
        const labels = {
            products: 'product price cache',
            masters: 'master-data cache',
            all: 'all cache'
        };

        if (!window.confirm(`Are you sure you want to clear ${labels[type]}?`)) {
            return;
        }

        setClearing(true);
        try {
            const endpoint = type === 'products'
                ? '/cache/clear-products'
                : type === 'masters'
                    ? '/cache/clear-masters'
                    : '/cache/clear-all';
            const response = await axios.post(`${apiUrl}${endpoint}`);
            if (response.data.success) {
                notify(`${labels[type]} cleared successfully`, { type: 'success' });
                // Refresh status after clearing
                setTimeout(() => {
                    fetchCacheStatus();
                }, 1000);
            } else {
                notify(`Failed to clear ${labels[type]}`, { type: 'error' });
            }
        } catch (error: any) {
            console.error('Error clearing cache:', error);
            notify('Error clearing cache: ' + (error.response?.data?.message || error.message), { type: 'error' });
        } finally {
            setClearing(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box p={3} maxWidth={800}>
            <Title title="Cache Settings" />
            
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">
                            Cache Status
                        </Typography>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={fetchCacheStatus}
                            disabled={loading}
                            size="small"
                        >
                            Refresh Status
                        </Button>
                    </Box>
                    
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={enabled}
                                    onChange={() => {}} // Read-only, just displays status
                                    disabled={true}
                                />
                            }
                            label={enabled ? 'Cache Enabled' : 'Cache Disabled'}
                        />
                    </FormGroup>

                    {cacheStatus && (
                        <Box mt={2}>
                            <Box display="flex" gap={2} mb={2} flexWrap="wrap">
                                <Chip 
                                    label={cacheStatus.connected ? 'Connected' : 'Disconnected'} 
                                    color={cacheStatus.connected ? 'success' : 'error'}
                                    size="small"
                                />
                                <Chip 
                                    label={cacheStatus.ready ? 'Ready' : 'Not Ready'} 
                                    color={cacheStatus.ready ? 'success' : 'warning'}
                                    size="small"
                                />
                                <Chip 
                                    label={`${cacheStatus.totalKeys || 0} Keys`} 
                                    color="primary"
                                    size="small"
                                />
                            </Box>

                            {cacheStatus.keysByType && (
                                <Box mt={2}>
                                    <Typography variant="body2" color="text.secondary">
                                        Cache Keys by Type:
                                    </Typography>
                                    <Box display="flex" gap={2} mt={1} flexWrap="wrap">
                                        {Object.entries(cacheStatus.keysByType).map(([key, value]) => (
                                            <Typography variant="body2" key={key}>
                                                {key}: <strong>{value || 0}</strong>
                                            </Typography>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Clear Cache
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Clear cache groups on the App API. The cache will be repopulated as new requests come in.
                    </Typography>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button
                            variant="contained"
                            color="warning"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleClearCache('products')}
                            disabled={clearing}
                        >
                            {clearing ? 'Clearing...' : 'Clear Product Prices'}
                        </Button>
                        <Button
                            variant="contained"
                            color="warning"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleClearCache('masters')}
                            disabled={clearing}
                        >
                            {clearing ? 'Clearing...' : 'Clear Master Data'}
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleClearCache('all')}
                            disabled={clearing}
                        >
                            {clearing ? 'Clearing...' : 'Clear All Cache'}
                        </Button>
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    );
};

export default CacheSettings;
